import { message } from 'antd';
import axios, {
  AxiosHeaders,
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
} from 'axios';
import { ENV } from '@/config';
import { API_TIMEOUT } from '@/constants';
import {
  clearAuthSession,
  getImpersonatedRental,
  refreshAccessToken,
  setPasswordChangeRequired,
} from '@/services/auth';
import { decodeApiError } from '@/utils/apiError';
import { captureError } from '@/services/telemetry';
import { buildLoginPath } from '@/utils/loginPath';
import {
  IMPERSONATION_REQUIRED_MESSAGE,
  isImpersonationRequiredError,
  isPasswordChangeRequiredError,
  RATE_LIMIT_MESSAGE,
  SERVER_ERROR_MESSAGE,
} from '@/utils/apiErrorMessage';
import { ROUTES } from '@/constants/routes';

export type ErrorBody = {
  statusCode?: number;
  _tag?: string;
  code?: string;
  message?: string;
  error?: string;
  details?: unknown;
};

export type ErrorResponse = {
  message?: string;
  error?: string | ErrorBody;
  details?: unknown;
};

const SKIP_AUTH_HEADER = 'X-Skip-Auth';
const SKIP_ERROR_MESSAGE_HEADER = 'X-Skip-Error-Message';

const hasSkipErrorMessageHeader = (headers: unknown): boolean => {
  if (!headers) return false;
  if (headers instanceof AxiosHeaders) return Boolean(headers.get(SKIP_ERROR_MESSAGE_HEADER));
  if (typeof headers === 'object') {
    return Boolean((headers as Record<string, unknown>)[SKIP_ERROR_MESSAGE_HEADER]);
  }
  return false;
};

const isPublicAuthRequest = (url: string | undefined): boolean =>
  typeof url === 'string' &&
  ['/auth/login', '/auth/request-password-reset', '/auth/reset-password'].some((path) =>
    url.includes(path),
  );

// The session can't be recovered: clear it and reload into /login. The hard
// navigation would destroy a toast, so Login explains why from `?reason=`.
// Concurrent failures all land here; repeating the same redirect is harmless.
const redirectToSessionEnded = (): void => {
  clearAuthSession();
  window.location.href = buildLoginPath(`${window.location.pathname}${window.location.search}`, {
    sessionEnded: true,
  });
};

const getErrorMessage = (error: AxiosError<ErrorResponse>): string => {
  // Network or timeout errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timeout. Please check your connection and try again.';
    }
    return 'Network error. Please check your connection and try again.';
  }

  // API error responses
  const { status, data } = error.response;
  if (status >= 500 && status <= 599 && status !== 503) return SERVER_ERROR_MESSAGE;

  // Try to get error message from response (backend wraps in 'error' object)
  const errorMessage =
    decodeApiError(error)?.message ??
    (data && typeof data.error === 'string' ? data.error : undefined);

  switch (status) {
    case 400:
      return errorMessage ?? 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      if (isImpersonationRequiredError(error)) return IMPERSONATION_REQUIRED_MESSAGE;
      return errorMessage ?? 'You do not have permission to perform this action.';
    case 404:
      return errorMessage ?? 'The requested resource was not found.';
    case 409:
      return errorMessage ?? 'A conflict occurred. The resource may already exist.';
    case 422:
      return errorMessage ?? 'Validation error. Please check your input and try again.';
    case 429:
      return RATE_LIMIT_MESSAGE;
    case 503:
      return errorMessage ?? 'Service temporarily unavailable. Please try again later.';
    default:
      return errorMessage ?? 'An unexpected error occurred. Please try again.';
  }
};

const createApiClient = (): AxiosInstance => {
  // In development, use relative URLs to leverage Vite proxy
  // In production, use full backend URL
  const baseURL = import.meta.env.DEV
    ? `/api/${ENV.apiVersion}`
    : `${ENV.apiBaseUrl}/api/${ENV.apiVersion}`;

  const client = axios.create({
    baseURL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - Add auth token and impersonation header
  client.interceptors.request.use(
    (config) => {
      const skipAuth = Boolean(config.headers.get(SKIP_AUTH_HEADER));
      config.headers.delete(SKIP_AUTH_HEADER);

      if (!skipAuth) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add the unchanged impersonation header for platform admins. A request that
        // names its own rental (an Admin reading a transfer's destination) keeps it;
        // the session's impersonation is never changed for it.
        const impersonatedRental = getImpersonatedRental();
        if (impersonatedRental && !config.headers.get('X-Impersonate-Rental')) {
          config.headers['X-Impersonate-Rental'] = impersonatedRental.id;
        }
      }

      return config;
    },
    (error: Error) => Promise.reject(error),
  );

  // Response interceptor - Handle errors
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ErrorResponse>) => {
      // Ignore canceled/aborted requests (e.g., from AbortController)
      // These are intentional cancellations, not errors
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }

      if (!error.response || error.response.status >= 500) {
        captureError(error, {
          method: error.config?.method?.toUpperCase() ?? null,
          path: new URL(client.getUri(error.config), window.location.origin).pathname,
          status: error.response?.status ?? null,
        });
      }

      const errorMessage = getErrorMessage(error);

      // Handle 401 - Try refresh once, then logout
      if (error.response?.status === 401) {
        const originalRequest = error.config as
          | (AxiosRequestConfig & { _retry?: boolean })
          | undefined;
        if (!originalRequest) {
          redirectToSessionEnded();
          return Promise.reject(error);
        }
        // Public auth requests (login, password-reset) legitimately 401 on bad
        // credentials or an invalid/expired token. Do NOT treat that as a
        // session-expiry: no refresh, no clearAuthSession, and crucially no hard
        // redirect to /login (which reloads the page and wipes the page's own
        // form before it can render its own error state). Let the page surface it.
        //
        // The logout request isn't public, but gets the same treatment for a
        // different reason: it can 401 when the access token is already
        // expired at click-time (e.g. an idle tab). No refresh-then-retry and
        // no hard redirect there either - both would race the best-effort
        // logout flow's own clear + redirect.
        if (
          isPublicAuthRequest(originalRequest.url) ||
          (typeof originalRequest.url === 'string' && originalRequest.url.includes('/auth/logout'))
        ) {
          return Promise.reject(error);
        }
        // A refresh 401 means the refresh token is revoked or expired. Its
        // callers (the retry below, authProvider, logout) each handle that, so
        // don't toast or retry here.
        if (
          typeof originalRequest.url === 'string' &&
          originalRequest.url.includes('/auth/refresh')
        ) {
          return Promise.reject(error);
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          // Only a failed refresh ends the session. The resent request runs through this
          // interceptor again, so its own failure (a 404, a wrong current password)
          // reaches the caller.
          const session = await refreshAccessToken().catch(() => null);
          if (!session) {
            redirectToSessionEnded();
            return Promise.reject(error);
          }
          const nextHeaders = new AxiosHeaders();
          if (originalRequest.headers) {
            nextHeaders.set(originalRequest.headers as AxiosRequestHeaders | AxiosHeaders);
          }
          nextHeaders.set('Authorization', `Bearer ${session.token}`);
          originalRequest.headers = nextHeaders;
          return client(originalRequest);
        }

        // A retried non-auth endpoint can still return 401 for domain reasons
        // (e.g. missing tenant context / RBAC, a wrong current password). Don't force
        // logout in that case, and leave the message to a caller that shows its own.
        if (!hasSkipErrorMessageHeader(originalRequest.headers)) {
          void message.error(errorMessage);
        }
        return Promise.reject(error);
      }

      // A password someone else set must be changed before any other route answers
      // (backend #274): send the user to the change page instead of a toast.
      if (isPasswordChangeRequiredError(error)) {
        setPasswordChangeRequired(true);
        if (window.location.pathname !== ROUTES.CHANGE_PASSWORD) {
          window.location.assign(ROUTES.CHANGE_PASSWORD);
        }
        return Promise.reject(error);
      }

      // For other errors, show user-friendly message
      // Don't show message for errors in components that handle their own error display
      if (!hasSkipErrorMessageHeader(error.config?.headers)) {
        void message.error(errorMessage);
      }

      // Add user-friendly message to error object
      const enhancedError = error as AxiosError<ErrorResponse> & { userMessage?: string };
      enhancedError.userMessage = errorMessage;

      return Promise.reject(enhancedError);
    },
  );

  return client;
};

export const apiClient = createApiClient();
